import { PvpFlag } from "../zones/zones.ts";
import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU16, readU16, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16 } from "./limits.ts";

/**
 * The flag toggle on the wire (ADR-076, PROTOCOL.md 5.10 and 5.11). `pvpFlagToggle` is the player's request, 4 bytes
 * naming the PvP preference it wants, so a request sent twice never flips the wrong way; `pvpSwitch` is the map
 * server's answer to its own player, 7 bytes: the ticks left in the switch it counts, the preference it counts toward
 * and a note saying why a request was refused or a switch interrupted. Encoders write into caller-owned buffers, so a
 * send allocates nothing; decoders never throw (ADR-030).
 */

/**
 * Total `pvpFlagToggle` frame length.
 */
export const PVP_FLAG_TOGGLE_BYTES = 4;

/**
 * Total `pvpSwitch` frame length.
 */
export const PVP_SWITCH_BYTES = 7;

/**
 * Why a request was refused or a switch interrupted (PROTOCOL.md 5.11): in combat or Combatant, in a Hostile zone, dead,
 * or, toward Protected, outside a safe zone (ADR-076's amendment of 2026-10-01), or, toward Flag on, below the PvP level (8, ADR-076's
 * amendment of 2026-10-08). `None` says nothing went wrong: a
 * switch started, was cancelled or ended.
 */
export const SwitchNote = { None: 0, RefusedCombat: 1, RefusedHostile: 2, RefusedDead: 3, InterruptedCombat: 4, InterruptedHostile: 5, RefusedUnsafe: 6, InterruptedUnsafe: 7, RefusedLevel: 8, RefusedParty: 9 } as const;
export type SwitchNote = (typeof SwitchNote)[keyof typeof SwitchNote];

/**
 * The highest note this build numbers.
 */
const LAST_NOTE = SwitchNote.RefusedParty;

/**
 * The note byte's top bit: the switch the owner could start now would complete at once (ADR-076's amendment of
 * 2026-10-07, PROTOCOL.md 5.11). A page that does not number the bit reads the byte as a note it does not know,
 * which is none.
 */
const INSTANT_BIT = 0x80;

/**
 * A decoded or to-be-encoded `pvpFlagToggle`: the preference wanted, `PvpFlag.Protected` or `PvpFlag.Flagged`.
 */
export interface PvpFlagToggleRecord {
  wanted: number;
}

/**
 * A decoded or to-be-encoded `pvpSwitch`: the ticks left in the switch (0 when none counts), the preference it counts
 * toward, the note, and whether a switch started now would complete at once (the character stands in a safe zone, out
 * of combat).
 */
export interface PvpSwitchRecord {
  ticks: number;
  toward: number;
  note: number;
  instant: boolean;
}

/**
 * Whether a value is one of the two preferences.
 *
 * @param value The value.
 * @returns True for `PvpFlag.Protected` and `PvpFlag.Flagged`.
 */
function isPreference(value: number): boolean {
  return value === PvpFlag.Protected || value === PvpFlag.Flagged;
}

/**
 * A fresh record for `decodePvpFlagToggle` to fill.
 *
 * @returns A record wanting Protected.
 */
export function newPvpFlagToggleRecord(): PvpFlagToggleRecord {
  return { wanted: PvpFlag.Protected };
}

/**
 * A fresh record for `decodePvpSwitch` to fill.
 *
 * @returns A record with no switch counting and no note.
 */
export function newPvpSwitchRecord(): PvpSwitchRecord {
  return { ticks: 0, toward: PvpFlag.Protected, note: SwitchNote.None, instant: false };
}

/**
 * Encodes a `pvpFlagToggle` into a caller-owned buffer.
 *
 * @param buffer Destination, at least `PVP_FLAG_TOGGLE_BYTES` long.
 * @param record The preference wanted.
 * @returns Bytes written, always `PVP_FLAG_TOGGLE_BYTES`.
 * @throws Error when the buffer is too small or the preference wanted is neither Protected nor Flagged.
 */
export function encodePvpFlagToggle(buffer: Uint8Array, record: Readonly<PvpFlagToggleRecord>): number {
  if (buffer.length < PVP_FLAG_TOGGLE_BYTES) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${PVP_FLAG_TOGGLE_BYTES}-byte pvpFlagToggle`);
  }
  if (!isPreference(record.wanted)) {
    throw new Error(`pvpFlagToggle wanted ${record.wanted} is neither Protected nor Flagged`);
  }
  putHeader(buffer, MessageType.PvpFlagToggle);
  buffer[HEADER_BYTES] = record.wanted;
  return PVP_FLAG_TOGGLE_BYTES;
}

/**
 * Decodes a `pvpFlagToggle` frame into a record. Never throws: a wrong length or type, or a wanted byte that is
 * neither preference, is malformed and a wrong version a version mismatch, leaving the record untouched.
 *
 * @param frame The frame.
 * @param out The record to fill.
 * @returns The verdict.
 */
export function decodePvpFlagToggle(frame: Uint8Array, out: PvpFlagToggleRecord): DecodeVerdict {
  const header = checkHeader(frame, MessageType.PvpFlagToggle, PVP_FLAG_TOGGLE_BYTES);
  if (header.kind !== "ok") {
    return header;
  }
  const wanted = frame[HEADER_BYTES] as number;
  if (!isPreference(wanted)) {
    return MALFORMED;
  }
  out.wanted = wanted;
  return OK;
}

/**
 * Encodes a `pvpSwitch` into a caller-owned buffer.
 *
 * @param buffer Destination, at least `PVP_SWITCH_BYTES` long.
 * @param record The values.
 * @returns Bytes written, always `PVP_SWITCH_BYTES`.
 * @throws Error when the buffer is too small, the ticks are not a u16, the preference is neither Protected nor Flagged,
 *   or the note is not one this build numbers.
 */
export function encodePvpSwitch(buffer: Uint8Array, record: Readonly<PvpSwitchRecord>): number {
  if (buffer.length < PVP_SWITCH_BYTES) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${PVP_SWITCH_BYTES}-byte pvpSwitch`);
  }
  assertField(record.ticks, MAX_U16, `pvpSwitch ticks ${record.ticks}`);
  if (!isPreference(record.toward)) {
    throw new Error(`pvpSwitch toward ${record.toward} is neither Protected nor Flagged`);
  }
  assertField(record.note, LAST_NOTE, `pvpSwitch note ${record.note}`);
  putHeader(buffer, MessageType.PvpSwitch);
  putU16(buffer, HEADER_BYTES, record.ticks);
  buffer[HEADER_BYTES + 2] = record.toward;
  buffer[HEADER_BYTES + 3] = record.instant ? record.note | INSTANT_BIT : record.note;
  return PVP_SWITCH_BYTES;
}

/**
 * Decodes a `pvpSwitch` frame into a record. Never throws: a wrong length or type is malformed and a wrong version a
 * version mismatch, leaving the record untouched. A preference other than Flagged reads as Protected, a note this
 * build does not number as none, and the note byte's top bit as `instant`.
 *
 * @param frame The frame.
 * @param out The record to fill.
 * @returns The verdict.
 */
export function decodePvpSwitch(frame: Uint8Array, out: PvpSwitchRecord): DecodeVerdict {
  const header = checkHeader(frame, MessageType.PvpSwitch, PVP_SWITCH_BYTES);
  if (header.kind !== "ok") {
    return header;
  }
  const byte = frame[HEADER_BYTES + 3] as number;
  const note = byte & ~INSTANT_BIT;
  out.instant = (byte & INSTANT_BIT) !== 0;
  out.ticks = readU16(frame, HEADER_BYTES);
  out.toward = frame[HEADER_BYTES + 2] === PvpFlag.Flagged ? PvpFlag.Flagged : PvpFlag.Protected;
  out.note = note <= LAST_NOTE ? note : SwitchNote.None;
  return OK;
}
